import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row } from "antd";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  getDynamicFieldsErrors,
  getFieldErrorProps,
} from "@/modules/form-validation";

import AdditionalCostsFormHeader from "./AdditionalCostsFormHeader";
import AdditionalCostsFormRow from "./AdditionalCostsFormRow";

const { useForm } = Form;

const AdditionalCostsForm = ({
  mutation,
  initialValues,
  onSuccess,
  onTotalChange = (_) => undefined,
}) => {
  const { t } = useTranslation();
  const { mutate, error } = mutation;
  const [form] = useForm();

  const defaultValues = {
    estimated_cost: 0,
  };

  const errors = getDynamicFieldsErrors(error, {
    additional_costs: [
      "type",
      "estimated_cost",
      "precision",
      "source",
      "description",
    ],
  });

  const onSave = useCallback((values) => {
    const additional_costs = values.map(
      ({ type, estimated_cost, precision, source, description }) => {
        return {
          type,
          estimated_cost,
          precision,
          source,
          description,
        };
      }
    );

    mutate({ additional_costs }, { onSuccess });
  }, []);

  const updateEstimatedCost = () => {
    const values = form.getFieldsValue().values;

    const estimated_cost = values.reduce(
      (accumulator, { estimated_cost }) => accumulator + estimated_cost,
      0
    );

    onTotalChange(estimated_cost);
  };

  useEffect(() => {
    updateEstimatedCost();
  }, []);

  return (
    <>
      <Form
        form={form}
        name="additionalCostsForm"
        onFinish={(values) => onSave(values.values)}
        onValuesChange={updateEstimatedCost}
        autoComplete="off"
        initialValues={{
          values: initialValues,
        }}
        className="additional-costs-form"
      >
        <AdditionalCostsFormHeader />
        <Form.List name="values">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <AdditionalCostsFormRow
                  name={name}
                  initialType={initialValues?.[index]?.type}
                  onRemove={() => remove(name)}
                  errors={errors?.additional_costs?.[index]}
                  {...restField}
                  key={key}
                />
              ))}
              {errors.others && (
                <Row className="additional-costs-row">
                  <Col span={24}>
                    <Form.Item {...getFieldErrorProps(errors.others)} />
                  </Col>
                </Row>
              )}
              <Row className="additional-costs-row">
                <Col span={24}>
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add(defaultValues)}
                      block
                      icon={<PlusOutlined />}
                    >
                      {t("project:additionalCosts.addNewCost")}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form.List>
      </Form>
    </>
  );
};

export default AdditionalCostsForm;
